import React, { useRef, useState } from 'react';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { useUpload, formatBytes, fileIcon } from '../context/UploadContext';

export default function UploadModal() {
  const { files, view, activeCount, addFiles, requestClose, minimize, cancelFile, removeFile } = useUpload();
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const allDone = files.length > 0 && activeCount === 0;

  return (
    <Modal
      open={view === 'open'}
      onClose={requestClose}
      icon="upload"
      title="Upload files"
      subtitle="Select and upload the files of your choice"
      footer={
        files.length === 0 ? (
          <Button kind="ghost" onClick={requestClose}>Cancel</Button>
        ) : allDone ? (
          <Button kind="accent" onClick={requestClose}>Done</Button>
        ) : (
          <Button kind="ghost" icon="minus" onClick={minimize}>Minimize</Button>
        )
      }
    >
      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={e => {
          e.preventDefault();
          setOver(false);
          const dropped = Array.from(e.dataTransfer.files);
          if (dropped.length) addFiles(dropped);
        }}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `1.5px dashed ${over ? 'var(--accent)' : 'var(--line-strong)'}`,
          borderRadius: 'var(--r-4)',
          padding: '40px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: over ? 'var(--accent-soft)' : 'transparent',
          transition: 'all 0.15s',
          marginBottom: files.length ? 16 : 0,
        }}
      >
        <span
          className="t-btn t-btn--outline"
          style={{ pointerEvents: 'none', height: 40, padding: '0 20px', fontSize: 'var(--fs-md)' }}
        >
          <Icon name="upload" size={18} />
          Upload
        </span>
        <div style={{ marginTop: 16, fontWeight: 600, fontSize: 'var(--fs-md)' }}>
          {over ? 'Drop to upload' : 'Choose a file or drag & drop it here'}
        </div>
        <input ref={inputRef} type="file" multiple hidden onChange={e => { if (e.target.files?.length) addFiles(Array.from(e.target.files)); }} />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 280, overflowY: 'auto' }}>
          {files.map(f => {
            const loaded = Math.round((f.file.size * f.progress) / 100);
            const inFlight = f.stage === 'uploading' || f.stage === 'completing' || f.stage === 'scanning';
            return (
            <div key={f.id} style={{ display: 'flex', gap: 12, padding: '12px 14px', background: 'var(--card)', borderRadius: 'var(--r-3)', border: '1px solid var(--line)' }}>
              <span
                style={{
                  position: 'relative', width: 40, height: 40, borderRadius: 'var(--r-2)', flexShrink: 0, overflow: 'hidden',
                  background: f.posterUrl ? '#000' : 'var(--accent-soft)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                {f.posterUrl ? (
                  <>
                    <img src={f.posterUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)' }}>
                      <Icon name="video" size={14} color="#fff" />
                    </span>
                  </>
                ) : (
                  <Icon name={fileIcon(f.file.type)} size={18} color="var(--accent-ink)" />
                )}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, fontSize: 'var(--fs-md)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</span>
                  {inFlight && (
                    <Button kind="ghost" size="sm" icon="x" iconOnly aria-label="Cancel upload" onClick={() => cancelFile(f.id)} />
                  )}
                  {f.stage === 'done' && (
                    <Button kind="ghost" size="sm" icon="trash" iconOnly aria-label="Remove" onClick={() => removeFile(f.id)} />
                  )}
                  {f.stage === 'error' && (
                    <Button kind="ghost" size="sm" icon="x" iconOnly aria-label="Dismiss" onClick={() => removeFile(f.id)} />
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>
                    {f.stage === 'uploading' ? `${formatBytes(loaded)} / ${formatBytes(f.file.size)}` : formatBytes(f.file.size)}
                  </span>
                  <span aria-hidden="true">•</span>
                  {f.stage === 'uploading' && <span style={{ color: 'var(--accent-ink)', fontWeight: 500 }}>Uploading…</span>}
                  {f.stage === 'completing' && <span>Finalizing…</span>}
                  {f.stage === 'scanning' && <span>Creating item…</span>}
                  {f.stage === 'done' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--success)', fontWeight: 500 }}><Icon name="check" size={13} color="var(--success)" />Completed</span>}
                  {f.stage === 'error' && <span style={{ color: 'var(--danger)', fontWeight: 500 }}>{f.error || 'Failed'}</span>}
                  {(f.stage === 'completing' || f.stage === 'scanning') && <span className="t-spinner" style={{ width: 12, height: 12, marginLeft: 2 }} />}
                </div>
                {f.stage === 'uploading' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                    <div className="t-progress" style={{ flex: 1 }}>
                      <div className="t-progress-fill t-progress-fill--gradient" style={{ width: `${f.progress}%` }} />
                    </div>
                    <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, minWidth: 34, textAlign: 'right' }}>{f.progress}%</span>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
